<script>
import { createProduct, getProductAccountList, uploadProductImage } from '@/api/product.js'
import { getCategoryList } from '@/api/category.js'
import { getBrandList } from '@/api/brand.js'
import { getSellerList } from '@/api/product.js'
import { getProductLabels } from '@/api/productLabel.js'
import { getProductDeliveryExchangeTemplates } from '@/api/productDeliveryExchangeTemplate.js'
import { getAdminInfo } from '@/utils/token.js'
import { IMAGE_SPECS, UPLOAD_ACCEPT, summaryText } from '@/constants/imageSpecs.js'

export default {
  name: 'ProductCreatePage',

  computed: {
    listImageSpec() { return summaryText('PRODUCT_LIST') },
    detailImageSpec() { return summaryText('PRODUCT_DETAIL') },
  },

  data() {
    return {
      UPLOAD_ACCEPT,
      LIST_IMAGE_NOTE: IMAGE_SPECS.PRODUCT_LIST.safeArea + ' ' + IMAGE_SPECS.PRODUCT_LIST.note,
      DETAIL_IMAGE_NOTE: IMAGE_SPECS.PRODUCT_DETAIL.note,
      form: {
        productName: '',
        categoryCode: '',
        brandCode: '',
        sellerCode: '',
        deliveryExchangeTemplateCode: '',
        productText: '',
        currency: 'KRW',
        conventionCost: null,
        weight: null,
        purity: null,
        weightEditableYn: 'Y',
        orderby: null,
        skuCode: '',
        stockQty: 0,
        productStatus: 'ON_SALE',
        stockStatus: 'IN_STOCK',
        priceDisplayType: 'MARKET',
        discountType: 'NONE',
        discountRate: null,
        discountAmount: null,
        originalPrice: null,
        discountLabel: '',
        manufacturer: '',
        originCountry: '',
        searchKeywords: '',
        labelCodes: [],
        paymentTypes: ['CASH', 'CARD'],
        cashBranchAccountCode: '',
      },
      categoryOptions: [{ title: '카테고리 선택', value: '' }],
      brandOptions: [{ title: '브랜드 선택', value: '' }],
      sellerOptions: [{ title: '공급사 선택', value: '' }],
      deliveryExchangeTemplateOptions: [{ title: '배송/교환 안내 미선택', value: '' }],
      labelOptions: [],
      productStatusOptions: [
        { title: '판매중', value: 'ON_SALE' },
        { title: '숨김', value: 'HIDDEN' },
        { title: '판매종료', value: 'ENDED' },
      ],
      stockStatusOptions: [
        { title: '재고있음', value: 'IN_STOCK' },
        { title: '품절', value: 'OUT_OF_STOCK' },
        { title: '입고예정', value: 'COMING_SOON' },
        { title: '판매중지', value: 'STOPPED' },
      ],
      priceDisplayTypeOptions: [
        { title: '시세 연동', value: 'MARKET' },
        { title: '고정가', value: 'FIXED' },
        { title: '가격문의', value: 'CONTACT' },
      ],
      discountTypeOptions: [
        { title: '없음', value: 'NONE' },
        { title: '상품가 할인', value: 'PRODUCT_PRICE' },
        { title: '마진 할인', value: 'MARGIN' },
      ],
      paymentTypeOptions: [
        { title: '계좌이체', value: 'CASH' },
        { title: '카드결제', value: 'CARD' },
      ],
      cashAccountOptions: [{ title: '입금계좌 선택', value: '' }],
      thumbnailFiles: [],
      detailFiles: [],
      saving: false,
      uploading: false,
      saveErrorMsg: '',
      adminInfo: {},
    }
  },

  computed: {
    isHq() {
      return this.adminInfo?.adminType === 'HQ'
    },
  },

  mounted() {
    this.adminInfo = getAdminInfo()
    Promise.all([this.fetchCategories(), this.fetchBrands(), this.fetchSellers(), this.fetchLabels(), this.fetchDeliveryExchangeTemplates(), this.fetchCashAccounts()])
  },

  beforeUnmount() {
    ;[...this.thumbnailFiles, ...this.detailFiles].forEach(file => {
      if (file?._previewUrl) URL.revokeObjectURL(file._previewUrl)
    })
  },

  methods: {
    async fetchCategories() {
      try {
        const res = await getCategoryList({ page: 1, size: 100 })
        if (res.data?.code === 0) {
          const list = [...(res.data.data?.items || [])].sort(this.sortByOrder)
          this.categoryOptions = [
            { title: '카테고리 선택', value: '' },
            ...list.map(item => ({ title: item.categoryName, value: item.categoryCode })),
          ]
        }
      } catch (error) {
        console.error('[ProductCreatePage] fetchCategories error:', error)
      }
    },

    async fetchSellers() {
      try {
        const res = await getSellerList()
        if (res.data?.code === 0) {
          this.sellerOptions = [
            { title: '공급사 선택', value: '' },
            ...(res.data.items || []).map(item => ({ title: item.sellerName, value: item.sellerCode })),
          ]
        }
      } catch (error) {
        console.error('fetchSellers error:', error)
      }
    },

    async fetchBrands() {
      try {
        const res = await getBrandList({ page: 1, size: 100, activeYn: 'Y' })
        if (res.data?.code === 0) {
          this.brandOptions = [
            { title: '브랜드 선택', value: '' },
            ...(res.data.data?.items || []).map(item => ({ title: item.brandName, value: item.brandCode })),
          ]
        }
      } catch (error) {
        console.error('[ProductCreatePage] fetchBrands error:', error)
      }
    },

    async fetchLabels() {
      try {
        const res = await getProductLabels({ activeYn: 'Y' })
        if (res.data?.code === 0) {
          this.labelOptions = (res.data.items || []).map(item => ({
            title: item.labelName,
            value: item.productLabelCode,
          }))
        }
      } catch (error) {
        console.error('[ProductCreatePage] fetchLabels error:', error)
      }
    },

    async fetchDeliveryExchangeTemplates() {
      try {
        const res = await getProductDeliveryExchangeTemplates({ activeYn: 'Y', page: 1, size: 200 })
        if (res.data?.code === 0) {
          const items = res.data.data?.items || res.data.items || []
          this.deliveryExchangeTemplateOptions = [
            { title: '배송/교환 안내 미선택', value: '' },
            ...items.map(item => ({
              title: `${item.scopeType === 'BRANCH' ? `[${item.branchName || '지점'}]` : '[본사]'} ${item.templateName}`,
              value: item.productDeliveryExchangeTemplateCode,
            })),
          ]
        }
      } catch (error) {
        console.error('[ProductCreatePage] fetchDeliveryExchangeTemplates error:', error)
      }
    },

    async fetchCashAccounts() {
      try {
        const res = await getProductAccountList({ activeYn: 'Y', page: 1, size: 500 })
        if (res.data?.code === 0) {
          const items = res.data.data?.items || []
          this.cashAccountOptions = [
            { title: '입금계좌 선택', value: '' },
            ...items.map(item => ({
              title: `${item.branchName || '본사/지점'} · ${item.bankName} ${item.accountNo} · ${item.accountHolder} (${this.formatAccountRange(item)})`,
              value: item.branchAccountCode,
            })),
          ]
        }
      } catch (error) {
        console.error('[ProductCreatePage] fetchCashAccounts error:', error)
      }
    },

    sortByOrder(a, b) {
      const ao = Number.isFinite(Number(a?.orderby)) ? Number(a.orderby) : Number.MAX_SAFE_INTEGER
      const bo = Number.isFinite(Number(b?.orderby)) ? Number(b.orderby) : Number.MAX_SAFE_INTEGER
      if (ao !== bo) return ao - bo
      return String(b?.insertDt || '').localeCompare(String(a?.insertDt || ''))
    },

    validate() {
      if (!this.form.productName?.trim()) return '상품명을 입력해주세요.'
      if (!this.form.categoryCode) return '카테고리를 선택해주세요.'
      if (!this.form.brandCode) return '브랜드를 선택해주세요.'
      if (!this.form.sellerCode) return '공급사를 선택해주세요.'
      if (!this.form.weight || Number(this.form.weight) <= 0) return '중량을 입력해주세요.'
      if (!this.form.purity || Number(this.form.purity) <= 0) return '순도를 입력해주세요.'
      if (Number(this.form.purity) > 1000) return '순도는 1000 이하로 입력해주세요.'
      if (this.form.orderby !== null && this.form.orderby !== '' && Number(this.form.orderby) < 0) return '정렬순서는 0 이상이어야 합니다.'
      if (!this.form.paymentTypes?.length) return '결제방법을 하나 이상 선택해 주세요.'
      if (this.form.paymentTypes.includes('CASH') && !this.form.cashBranchAccountCode) return '계좌이체 결제에 사용할 입금계좌를 선택해 주세요.'
      return ''
    },

    buildPayload() {
      return {
        productName: this.form.productName.trim(),
        categoryCode: this.form.categoryCode,
        brandCode: this.form.brandCode,
        sellerCode: this.form.sellerCode,
        deliveryExchangeTemplateCode: this.form.deliveryExchangeTemplateCode || null,
        productText: this.form.productText || null,
        currency: this.form.currency || null,
        conventionCost: this.toNumberOrNull(this.form.conventionCost),
        weight: Number(this.form.weight),
        purity: Number(this.form.purity),
        weightEditableYn: this.form.weightEditableYn || 'Y',
        orderby: this.toNumberOrNull(this.form.orderby),
        skuCode: this.form.skuCode || null,
        stockQty: this.toNumberOrNull(this.form.stockQty) ?? 0,
        productStatus: this.form.productStatus,
        stockStatus: this.form.stockStatus,
        priceDisplayType: this.form.priceDisplayType,
        discountType: this.form.discountType,
        discountRate: this.toNumberOrNull(this.form.discountRate),
        discountAmount: this.toNumberOrNull(this.form.discountAmount),
        originalPrice: this.toNumberOrNull(this.form.originalPrice),
        discountLabel: this.form.discountLabel || null,
        manufacturer: this.form.manufacturer || null,
        originCountry: this.form.originCountry || null,
        searchKeywords: this.form.searchKeywords || null,
        labelCodes: this.form.labelCodes,
        paymentTypes: this.form.paymentTypes,
        cashBranchAccountCode: this.form.paymentTypes.includes('CASH') ? this.form.cashBranchAccountCode : null,
      }
    },

    formatAccountRange(item) {
      const min = Number(item.minAmount || 0).toLocaleString('ko-KR')
      const max = item.maxAmount === null || item.maxAmount === undefined ? '제한 없음' : Number(item.maxAmount).toLocaleString('ko-KR')
      return `${min} ~ ${max}`
    },

    toNumberOrNull(value) {
      if (value === '' || value === null || value === undefined) return null
      const number = Number(value)
      return Number.isNaN(number) ? null : number
    },

    async handleCreate() {
      this.saveErrorMsg = this.validate()
      if (this.saveErrorMsg) return

      this.saving = true
      try {
        const res = await createProduct(this.buildPayload())
        if (res.data?.code !== 0) {
          this.saveErrorMsg = res.data?.message || '등록 중 오류가 발생했습니다.'
          return
        }

        const productCode = res.data?.productCode || res.data?.data?.productCode
        if (!productCode) {
          this.saveErrorMsg = '상품코드를 받지 못했습니다.'
          return
        }

        this.uploading = true
        for (const file of this.thumbnailFiles) await uploadProductImage(productCode, 'thumbnail', file)
        for (const file of this.detailFiles) await uploadProductImage(productCode, 'detail', file)

        this.$router.push(`/products/${productCode}`)
      } catch (error) {
        console.error('[ProductCreatePage] handleCreate error:', error)
        this.saveErrorMsg = error?.response?.data?.message || '등록 중 오류가 발생했습니다.'
      } finally {
        this.saving = false
        this.uploading = false
      }
    },

    onFileSelect(event, target) {
      const files = Array.from(event.target.files || [])
      this[target] = [...this[target], ...files]
      event.target.value = ''
    },

    removeFile(target, index) {
      const file = this[target][index]
      if (file?._previewUrl) URL.revokeObjectURL(file._previewUrl)
      this[target].splice(index, 1)
    },

    getPreviewUrl(file) {
      if (!file._previewUrl) file._previewUrl = URL.createObjectURL(file)
      return file._previewUrl
    },

    goBack() {
      this.$router.push('/products')
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="text-caption text-primary mb-1 breadcrumb-link" @click="goBack">상품관리 / 상품 등록</div>
        <div class="text-h5 font-weight-bold">상품 등록</div>
        <div class="text-body-2 text-grey mt-1">상품 기본정보, 판매상태, 라벨, 이미지를 등록합니다.</div>
      </div>
      <div class="d-flex ga-2">
        <v-btn variant="text" :disabled="saving" @click="goBack">취소</v-btn>
        <v-btn color="primary" :loading="saving || uploading" prepend-icon="mdi-check" @click="handleCreate">
          {{ uploading ? '이미지 업로드 중' : '등록' }}
        </v-btn>
      </div>
    </div>

    <v-alert v-if="saveErrorMsg" type="error" variant="tonal" density="compact" class="mb-4">
      {{ saveErrorMsg }}
    </v-alert>

    <v-card rounded="lg" elevation="1" class="mb-5">
      <v-card-title class="text-subtitle-1 font-weight-bold py-4">기본 정보</v-card-title>
      <v-divider />
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6"><v-text-field v-model="form.productName" label="상품명 *" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-select v-model="form.categoryCode" :items="categoryOptions" label="카테고리 *" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-select v-model="form.brandCode" :items="brandOptions" label="브랜드 *" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-select v-model="form.sellerCode" :items="sellerOptions" label="공급사 *" variant="outlined" hint="판매자는 아우라, 상품을 대는 법인" persistent-hint /></v-col>
          <v-col cols="12" md="6"><v-select v-model="form.deliveryExchangeTemplateCode" :items="deliveryExchangeTemplateOptions" label="배송/교환 안내" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="form.skuCode" label="SKU/판매코드" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="form.weight" type="number" step="0.001" label="중량 *" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="form.purity" type="number" step="0.01" label="순도 *" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model.number="form.conventionCost" type="number" label="임가공비" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="form.manufacturer" label="제조사/민트" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="form.originCountry" label="원산지" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model.number="form.orderby" type="number" min="0" label="정렬순서" variant="outlined" /></v-col>
          <v-col cols="12" md="6"><v-text-field v-model="form.searchKeywords" label="검색 키워드" variant="outlined" placeholder="쉼표로 구분" /></v-col>
          <v-col cols="12">
            <v-textarea v-model="form.productText" label="상품 설명" variant="outlined" rows="4" auto-grow />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1" class="mb-5">
      <v-card-title class="text-subtitle-1 font-weight-bold py-4">판매 설정</v-card-title>
      <v-divider />
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3"><v-select v-model="form.productStatus" :items="productStatusOptions" label="상품 상태 *" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-select v-model="form.stockStatus" :items="stockStatusOptions" label="재고 상태 *" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model.number="form.stockQty" type="number" min="0" step="0.001" label="재고 수량 *" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-select v-model="form.priceDisplayType" :items="priceDisplayTypeOptions" label="가격 표시" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-select v-model="form.discountType" :items="discountTypeOptions" label="할인 방식" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model.number="form.originalPrice" type="number" label="기존가" variant="outlined" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model.number="form.discountRate" type="number" label="할인율(%)" variant="outlined" :disabled="form.discountType === 'NONE'" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model.number="form.discountAmount" type="number" label="할인금액" variant="outlined" :disabled="form.discountType === 'NONE'" /></v-col>
          <v-col cols="12" md="3"><v-text-field v-model="form.discountLabel" label="할인 문구" variant="outlined" placeholder="예: -10%, 특가" /></v-col>
          <v-col cols="12" md="6"><v-select v-model="form.labelCodes" :items="labelOptions" label="상품 라벨" variant="outlined" chips multiple /></v-col>
          <v-col cols="12" md="6">
            <v-switch v-model="form.weightEditableYn" true-value="Y" false-value="N" color="primary" label="주문 시 중량 수기 수정 허용" hide-details />
          </v-col>
          <v-col cols="12">
            <div class="payment-box">
              <div class="text-subtitle-2 font-weight-bold mb-2">결제방법 *</div>
              <div class="d-flex flex-wrap ga-4">
                <v-checkbox
                  v-for="option in paymentTypeOptions"
                  :key="option.value"
                  v-model="form.paymentTypes"
                  :label="option.title"
                  :value="option.value"
                  color="primary"
                  hide-details
                  density="compact"
                />
              </div>
              <div class="text-caption text-medium-emphasis mt-2">
                계좌이체 선택 시 본사/지점의 입금계좌 관리에 상품금액을 포함하는 활성 계좌가 있어야 저장됩니다.
              </div>
              <v-select
                v-if="form.paymentTypes.includes('CASH')"
                v-model="form.cashBranchAccountCode"
                :items="cashAccountOptions"
                label="입금계좌 *"
                variant="outlined"
                density="compact"
                hide-details
                class="mt-3"
              />
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-card-title class="text-subtitle-1 font-weight-bold py-4">이미지</v-card-title>
      <v-divider />
      <v-card-text>
        <div class="image-section">
          <div class="font-weight-bold mb-2">목록 이미지</div>
          <div class="text-caption text-grey mb-1">{{ listImageSpec }}</div>
          <div class="text-caption text-grey mb-3">{{ LIST_IMAGE_NOTE }}</div>
          <div class="image-list">
            <div v-for="(file, index) in thumbnailFiles" :key="`thumb-${index}`" class="image-item">
              <v-img :src="getPreviewUrl(file)" width="120" height="120" cover rounded="lg" />
              <v-btn icon size="x-small" color="error" class="image-delete" @click="removeFile('thumbnailFiles', index)"><v-icon>mdi-close</v-icon></v-btn>
            </div>
            <input ref="thumbInput" type="file" :accept="UPLOAD_ACCEPT" multiple hidden @change="onFileSelect($event, 'thumbnailFiles')">
            <v-btn variant="tonal" prepend-icon="mdi-plus" @click="$refs.thumbInput?.click()">이미지 추가</v-btn>
          </div>
        </div>

        <div class="image-section mt-6">
          <div class="font-weight-bold mb-2">상세 이미지</div>
          <div class="text-caption text-grey mb-1">{{ detailImageSpec }}</div>
          <div class="text-caption text-grey mb-3">{{ DETAIL_IMAGE_NOTE }}</div>
          <div class="image-list">
            <div v-for="(file, index) in detailFiles" :key="`detail-${index}`" class="image-item">
              <v-img :src="getPreviewUrl(file)" width="120" height="120" cover rounded="lg" />
              <v-btn icon size="x-small" color="error" class="image-delete" @click="removeFile('detailFiles', index)"><v-icon>mdi-close</v-icon></v-btn>
            </div>
            <input ref="detailInput" type="file" :accept="UPLOAD_ACCEPT" multiple hidden @change="onFileSelect($event, 'detailFiles')">
            <v-btn variant="tonal" prepend-icon="mdi-plus" @click="$refs.detailInput?.click()">이미지 추가</v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
.breadcrumb-link {
  cursor: pointer;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.image-item {
  position: relative;
}

.image-delete {
  position: absolute;
  top: 4px;
  right: 4px;
}

.payment-box {
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}
</style>
